import { useNavigate, useParams } from 'react-router-dom';
import { useGetCompany } from '@/services/queries/company';
import {
	CreditCard,
	AlertCircle,
	ExternalLink,
	LayoutDashboard,
} from 'lucide-react';
import { PageTitle } from '@/components/ui/PageTitle';
import { CopyWrapper } from '@/components/ui/CopyWrapper';
import { useState } from 'react';
import { maskCNPJ, removeMask } from '@/utils/masks';

const formatCompanyDateTime = (iso?: string | null) => {
	if (!iso) return '—';
	try {
		return new Date(iso).toLocaleString('pt-BR', {
			day: '2-digit',
			month: '2-digit',
			year: 'numeric',
			hour: '2-digit',
			minute: '2-digit',
		});
	} catch {
		return String(iso);
	}
};

const getCompanyDisplayName = (company?: {
	trade_name?: string | null;
	legal_name?: string | null;
}) => company?.trade_name?.trim() || company?.legal_name?.trim() || 'Empresa';

export const AboutCompany = () => {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const [bannerImgError, setBannerImgError] = useState(false);
	const {
		data: company,
		isLoading,
		isError,
	} = useGetCompany(id || '', {
		refetchOnWindowFocus: true,
	});

	return (
		<section className="flex animate-fade-in flex-col gap-6">
			{/* Header */}
			<div className="flex items-center gap-6">
				<div className="flex min-w-0 flex-1 items-center justify-between gap-4">
					<div className="flex min-w-0 flex-1 flex-col gap-1">
						<PageTitle
							text={
								company
									? `Empresa: ${getCompanyDisplayName(
											company,
									  )}`
									: 'Empresa'
							}
						/>
					</div>
					{company?.banner_img_url && !bannerImgError && (
						<img
							src={company.banner_img_url}
							alt={getCompanyDisplayName(company)}
							className="h-14 w-24 flex-shrink-0 rounded border border-black/20 object-cover object-center"
							onError={() => setBannerImgError(true)}
						/>
					)}
				</div>
			</div>

			{id && (
				<div className="flex w-full flex-wrap items-center justify-between gap-x-4 gap-y-2">
					<div className="min-w-0 max-w-full">
						{company ? (
							<CopyWrapper
								valueToCopy={company.id}
								wrapperClassName="inline-flex w-fit max-w-full items-center gap-2 pr-8"
								buttonClassName="!right-0"
								tooltipText="ID copiado!"
							>
								<span className="shrink-0 text-[10px] font-black uppercase tracking-widest text-stone-400">
									ID da empresa
								</span>
								<span
									className="min-w-0 truncate font-mono text-sm font-semibold text-stone-700"
									title={company.id}
								>
									{company.id}
								</span>
							</CopyWrapper>
						) : null}
					</div>
					<div className="ml-auto flex shrink-0 flex-wrap items-center justify-end gap-2">
						<button
							type="button"
							onClick={() =>
								navigate('/admin/empresas?tab=planos')
							}
							title="Cadastro e gestão dos planos empresariais"
							className="inline-flex items-center gap-2 rounded-full border border-stone-200 bg-white px-4 py-2 text-xs font-bold text-stone-800 shadow-sm transition-colors hover:border-stone-300 hover:bg-stone-50"
						>
							<CreditCard className="size-3.5 text-stone-600" />
							Planos
						</button>
						<button
							type="button"
							onClick={() =>
								navigate(`/admin/empresas/${id}/dashboard`)
							}
							title="Painel da empresa"
							className="inline-flex items-center gap-2 rounded-full border border-stone-200 bg-white px-4 py-2 text-xs font-bold text-stone-800 shadow-sm transition-colors hover:border-stone-300 hover:bg-stone-50"
						>
							<LayoutDashboard className="size-3.5 text-stone-600" />
							Painel
						</button>
					</div>
				</div>
			)}

			<div className="space-y-6">
				{isLoading ? (
					<div className="flex h-40 items-center justify-center">
						<p className="animate-pulse text-sm font-black uppercase tracking-widest text-stone-400">
							Carregando...
						</p>
					</div>
				) : isError || !company ? (
					<div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-stone-200 bg-stone-50/50 py-24 text-center">
						<div className="mb-4 rounded-full bg-white p-4 text-stone-200 shadow-sm">
							<AlertCircle size={32} />
						</div>
						<h3 className="text-base font-bold text-stone-900">
							Empresa não encontrada
						</h3>
						<p className="mx-auto mt-1 max-w-xs text-sm leading-relaxed text-stone-400">
							Não foi possível localizar os dados desta empresa.
						</p>
					</div>
				) : (
					<>
						<div className="rounded-2xl border border-stone-100 bg-white p-5 shadow-sm">
							<p className="mb-4 text-[10px] font-black uppercase tracking-widest text-stone-400">
								Dados da empresa
							</p>
							<div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
								<div className="rounded-xl border border-stone-100 bg-stone-50/40 p-4">
									<p className="text-[10px] font-black uppercase tracking-widest text-stone-400">
										Nome
									</p>
									<p className="mt-1 text-sm font-semibold text-stone-900">
										{getCompanyDisplayName(company)}
									</p>
								</div>
								<div className="rounded-xl border border-stone-100 bg-stone-50/40 p-4">
									<p className="text-[10px] font-black uppercase tracking-widest text-stone-400">
										Razão social
									</p>
									<p className="mt-1 text-sm font-semibold text-stone-900">
										{company.legal_name || '—'}
									</p>
								</div>
								<div className="rounded-xl border border-stone-100 bg-stone-50/40 p-4">
									<p className="text-[10px] font-black uppercase tracking-widest text-stone-400">
										Nome fantasia
									</p>
									<p className="mt-1 text-sm font-semibold text-stone-900">
										{company.trade_name || '—'}
									</p>
								</div>
								<div className="rounded-xl border border-stone-100 bg-stone-50/40 p-4">
									<p className="text-[10px] font-black uppercase tracking-widest text-stone-400">
										CNPJ
									</p>
									{company.cnpj ? (
										<CopyWrapper
											valueToCopy={removeMask(
												company.cnpj,
											)}
											wrapperClassName="mt-1 pr-9"
											buttonClassName="!right-0"
											tooltipText="CNPJ copiado!"
										>
											<p className="font-mono text-sm font-semibold text-stone-900">
												{maskCNPJ(company.cnpj)}
											</p>
										</CopyWrapper>
									) : (
										<p className="mt-1 font-mono text-sm font-semibold text-stone-900">
											—
										</p>
									)}
								</div>
								<div className="rounded-xl border border-stone-100 bg-stone-50/40 p-4">
									<p className="text-[10px] font-black uppercase tracking-widest text-stone-400">
										E-mail de cobrança
									</p>
									<p className="mt-1 break-all text-sm font-semibold text-stone-900">
										{company.billing_email || '—'}
									</p>
								</div>
								<div className="rounded-xl border border-stone-100 bg-stone-50/40 p-4 md:col-span-2 xl:col-span-1">
									<p className="text-[10px] font-black uppercase tracking-widest text-stone-400">
										Status do cadastro
									</p>
									<p className="mt-1 text-sm font-semibold text-stone-900">
										<span
											className={
												company.active
													? 'font-black text-emerald-600'
													: 'font-black text-stone-500'
											}
										>
											{company.active
												? 'Ativa'
												: 'Inativa'}
										</span>
									</p>
								</div>
								<div className="rounded-xl border border-stone-100 bg-stone-50/40 p-4">
									<p className="text-[10px] font-black uppercase tracking-widest text-stone-400">
										Colaboradores (cadastro)
									</p>
									<p className="mt-1 text-sm font-semibold text-stone-900">
										{company._count?.employees ?? '—'}
									</p>
								</div>
								<div className="rounded-xl border border-stone-100 bg-stone-50/40 p-4">
									<p className="text-[10px] font-black uppercase tracking-widest text-stone-400">
										Registros de plano
									</p>
									<p className="mt-1 text-sm font-semibold text-stone-900">
										{company._count
											?.company_plan_subscription ?? '—'}
									</p>
								</div>
								<div className="rounded-xl border border-stone-100 bg-stone-50/40 p-4">
									<p className="text-[10px] font-black uppercase tracking-widest text-stone-400">
										Criada em
									</p>
									<p className="mt-1 text-sm font-semibold text-stone-900">
										{formatCompanyDateTime(
											company.created_at,
										)}
									</p>
								</div>
								<div className="rounded-xl border border-stone-100 bg-stone-50/40 p-4">
									<p className="text-[10px] font-black uppercase tracking-widest text-stone-400">
										Atualizada em
									</p>
									<p className="mt-1 text-sm font-semibold text-stone-900">
										{formatCompanyDateTime(
											company.updated_at,
										)}
									</p>
								</div>
								<div className="rounded-xl border border-stone-100 bg-stone-50/40 p-4">
									<p className="text-[10px] font-black uppercase tracking-widest text-stone-400">
										Código de validação
									</p>
									{company.validation_code ? (
										<CopyWrapper
											valueToCopy={
												company.validation_code
											}
											wrapperClassName="mt-1 pr-9"
											buttonClassName="!right-0"
											tooltipText="Código copiado!"
										>
											<p className="truncate font-mono text-sm font-semibold text-stone-900">
												{company.validation_code}
											</p>
										</CopyWrapper>
									) : (
										<p className="mt-1 text-sm font-semibold text-stone-900">
											—
										</p>
									)}
								</div>
								{company.stripe_customer_id ? (
									<div className="rounded-xl border border-stone-100 bg-stone-50/40 p-4">
										<p className="text-[10px] font-black uppercase tracking-widest text-stone-400">
											Cliente Stripe
										</p>
										<CopyWrapper
											valueToCopy={
												company.stripe_customer_id
											}
											wrapperClassName="mt-1 pr-9"
											buttonClassName="!right-0"
											tooltipText="Stripe ID copiado!"
										>
											<p
												className="truncate font-mono text-sm font-semibold text-stone-900"
												title={
													company.stripe_customer_id
												}
											>
												{company.stripe_customer_id}
											</p>
										</CopyWrapper>
									</div>
								) : (
									<div className="rounded-xl border border-stone-100 bg-stone-50/40 p-4">
										<p className="text-[10px] font-black uppercase tracking-widest text-stone-400">
											Cliente Stripe
										</p>
										<p className="mt-1 text-sm font-semibold text-stone-900">
											Não vinculado
										</p>
									</div>
								)}
								<div className="rounded-xl border border-stone-100 bg-stone-50/40 p-4 md:col-span-2 xl:col-span-3">
									<p className="mb-2 text-[10px] font-black uppercase tracking-widest text-stone-400">
										URL do banner
									</p>
									{company.banner_img_url ? (
										<div className="flex flex-wrap items-center gap-2">
											<a
												href={company.banner_img_url}
												target="_blank"
												rel="noopener noreferrer"
												className="inline-flex items-center gap-1 text-sm font-semibold text-indigo-600 underline-offset-2 hover:underline"
											>
												Abrir imagem
												<ExternalLink className="size-3.5 shrink-0" />
											</a>
											<CopyWrapper
												valueToCopy={
													company.banner_img_url
												}
												wrapperClassName="relative inline-flex min-w-0 max-w-full flex-1 items-center rounded-lg border border-stone-200 bg-white px-3 py-1.5 pr-9"
												buttonClassName="!right-1"
												tooltipText="URL copiada!"
											>
												<span
													className="truncate font-mono text-xs text-stone-700"
													title={
														company.banner_img_url
													}
												>
													{company.banner_img_url}
												</span>
											</CopyWrapper>
										</div>
									) : (
										<p className="text-sm font-semibold text-stone-900">
											—
										</p>
									)}
								</div>
								<div className="rounded-xl border border-stone-100 bg-stone-50/40 p-4 md:col-span-2 xl:col-span-3">
									<p className="mb-2 text-[10px] font-black uppercase tracking-widest text-stone-400">
										URL do logo
									</p>
									{company.logo_img_url ? (
										<div className="flex flex-wrap items-center gap-2">
											<a
												href={company.logo_img_url}
												target="_blank"
												rel="noopener noreferrer"
												className="inline-flex items-center gap-1 text-sm font-semibold text-indigo-600 underline-offset-2 hover:underline"
											>
												Abrir imagem
												<ExternalLink className="size-3.5 shrink-0" />
											</a>
											<CopyWrapper
												valueToCopy={
													company.logo_img_url
												}
												wrapperClassName="relative inline-flex min-w-0 max-w-full flex-1 items-center rounded-lg border border-stone-200 bg-white px-3 py-1.5 pr-9"
												buttonClassName="!right-1"
												tooltipText="URL copiada!"
											>
												<span
													className="truncate font-mono text-xs text-stone-700"
													title={company.logo_img_url}
												>
													{company.logo_img_url}
												</span>
											</CopyWrapper>
										</div>
									) : (
										<p className="text-sm font-semibold text-stone-900">
											—
										</p>
									)}
								</div>
							</div>
						</div>
					</>
				)}
			</div>
		</section>
	);
};
